import { useEffect, useMemo, useState } from 'react';
import { Badge, Select } from '@react-apps/design-system';
import { UFS } from '../../domain/entities/content.entity';
import type { Institution } from '../../domain/entities/content.entity';
import { useInstitutionsById } from '../hooks/use-institutions';

const UF_OPTIONS = UFS.map((uf) => ({ value: uf, label: uf }));

/**
 * Escolha da instituição de uma prova: **UF primeiro, instituição depois**.
 *
 * Substitui os dois campos de texto livre onde se digitava o UUID à mão. Não é
 * enfeite: um campo que aceita qualquer string aceita o id de uma instituição
 * **arquivada** (que o back recusa com `INSTITUTION_NOT_FOUND`) e aceita um id
 * que não existe. A UF na frente é o que mantém a segunda lista curta sem
 * depender de busca por texto — e é o mesmo eixo que a pessoa tem na cabeça
 * quando está olhando a prova.
 *
 * **Arquivada fica fora da lista** em qualquer caso: é o que "não se oferece
 * mais" quer dizer. A exceção é a prova que **já** aponta para uma arquivada:
 * aí ela aparece selecionada e marcada, trocar é possível, e voltar para ela
 * depois de trocar não é.
 */
export function InstitutionSelect({
  value,
  onChange,
  required,
}: {
  /** `institutionId` atual — string vazia quando ainda não escolhido. */
  value: string;
  onChange: (institutionId: string) => void;
  required?: boolean;
}) {
  const { data: institutionsById, isLoading } = useInstitutionsById();
  const [uf, setUf] = useState('');

  const selected = value ? institutionsById?.get(value) : undefined;

  // Editando uma prova existente, os dois nascem preenchidos: a UF vem da
  // instituição já resolvida.
  useEffect(() => {
    if (selected && !uf) setUf(selected.uf);
  }, [selected, uf]);

  const options = useMemo(() => {
    const all = [...(institutionsById?.values() ?? [])];
    const live = all
      .filter((i) => i.uf === uf && i.deletedAt === null)
      .sort((a, b) => a.initials.localeCompare(b.initials));
    // A arquivada que a prova já usa entra na lista só para o `<select>` poder
    // exibir a seleção atual — sem isso o campo apareceria vazio numa prova
    // que tem instituição.
    const current =
      selected && selected.deletedAt !== null && !live.includes(selected)
        ? [selected]
        : [];
    return [...current, ...live].map((i) => ({
      value: i.id,
      label: `${label(i)}${i.deletedAt ? ' — arquivada' : ''}`,
    }));
  }, [institutionsById, uf, selected]);

  return (
    <>
      <Select
        label="UF"
        value={uf}
        // A UF é filtro: não é enviada e não entra no `body` da prova. Trocar
        // de UF limpa a instituição — manter a anterior selecionada deixaria o
        // campo mostrando uma instituição que não está mais na lista.
        onChange={(e) => {
          setUf(e.target.value);
          if (value) onChange('');
        }}
        placeholder="UF da instituição"
        options={UF_OPTIONS}
        required={required}
      />
      <Select
        label="Instituição"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={
          uf
            ? options.length === 0
              ? 'Nenhuma instituição ativa nesta UF'
              : 'Escolha a instituição'
            : 'Escolha a UF primeiro'
        }
        options={options}
        disabled={!uf || isLoading}
        required={required}
        helperText={
          selected?.deletedAt
            ? 'Esta instituição está arquivada: dá para trocar, mas não para voltar a ela depois.'
            : undefined
        }
      />
      {selected?.deletedAt && <Badge variant="warning">Arquivada</Badge>}
    </>
  );
}

function label(institution: Institution): string {
  return `(${institution.initials}-${institution.uf}) ${institution.name}`;
}
